import { z } from 'zod';

export const AxonEventTypeSchema = z.enum([
  'agent.started',
  'agent.stopped',
  'agent.killed',
  'agent.interrupted',
  'agent.error',
  'agent.name',
  'agent.plan',
  'agent.progress',
  'agent.need_input',
  'agent.complete',
  'agent.pr_created',
  'agent.pr_merged',
  'agent.pr_closed',
  'agent.pr_checks_failed',
  'agent.pr_review',
  'agent.setup',
  'agent.tool_use',
  'agent.dev_server',
  'agent.performance_warning',
  'agent.daemon_started',
  // An agent asked for a capability a human must grant (a watch, an
  // interrupt, a destructive action); the card in the thread renders it and
  // `agent.capability_resolved` closes it in place.
  'agent.capability_requested',
  'agent.capability_resolved',
  // Structured walkthrough of a body of work, produced by the agent's
  // `work-assessment` skill and relayed through the sandbox bridge. The
  // document itself is plugin-owned (`@reflex/plugin-work-assessment`);
  // the host only carries it.
  'agent.work_assessment',
  // Claude Max credential refresh system events (origin: SYSTEM_EVENT)
  'agent.credentials_refreshed',
  'agent.credentials_refresh_failed',
  'agent.source.dashboard',
  'agent.source.sentry_issue',
  // TODO: the agent.source.* events are largely vestigial and unused.
  'agent.source.linear_issue',
  'agent.source.slack',
  'agent.source.flow',
  'agent.flow_result',
  'agent.subagent',
  'agent.compact',
  'agent.status_change',
  'agent.runtime_settings_changed',
  // Result of a native Codex slash command handled by the provider (origin:
  // SYSTEM_EVENT). Carries the command, outcome, and the sticky turn-override
  // snapshot after the command applied.
  'codex.command.result',
  // Broker stdout/stderr log lines emitted by the agent process.
  'agent.log',
  'flow.started',
  'flow.node_started',
  'flow.node_completed',
  'flow.node_error',
  'flow.node_retrying',
  'flow.node_skipped',
  'flow.approval_requested',
  'flow.approval_responded',
  'flow.completed',
  'flow.error',
  'flow.cancelled',
  // Crew team-thread events published onto a team's agentless stream by
  // `@reflex/plugin-crew`. The host only carries them; payload shapes are
  // plugin-owned and validated against the crew definition's schemas.
  'crew.task_dispatched',
  'crew.task_completed',
  'crew.task_failed',
  'crew.result_published',
  'crew.actor_post',
  'crew.gate_requested',
  'crew.gate_resolved',
  'crew.escalation',
  'message',
  'webhook',
  // Runloop turn protocol events (turn-based agent communication)
  'turn.started',
  'turn.message_chunk',
  'turn.tool_call',
  'turn.tool_call_update',
  'turn.completed',
  'turn.cancelled',
  'turn.failed',
  'turn.resumed',
  // Broker background-task tracking (SYSTEM_EVENT, source "broker")
  'background.changed',
  // Claude CLI raw protocol events (unprefixed)
  'system',
  'user',
  'assistant',
  'result',
  'error',
  'rate_limit_event',
  'control_request',
  'control_response',
  // ACP (Agent Client Protocol) events (unprefixed)
  'user_message_chunk',
  'agent_message_chunk',
  'agent_thought_chunk',
  'tool_call',
  'tool_call_update',
  'plan',
  'available_commands_update',
  'current_mode_update',
  'config_option_update',
  'session_info_update',
  'usage_update',
  // Devbox lifecycle system events (origin: SYSTEM_EVENT)
  'devbox.running',
  'devbox.suspended',
  'devbox.shutdown',
  'devbox.failed',
  // Devbox retention cleanup: the org's sandbox retention policy expired and
  // the host shut the devbox down. Carries the human-readable reason so the
  // agent stream can explain why the sandbox is gone and unusable.
  'devbox.cleaned_up',
  // Broker control events (sent to the broker to trigger out-of-band actions)
  'broker.control.interrupt',
  'broker.control.cancel',
  'broker.claude.control',
  'broker.acp.control',
  // Claude SDK input events (sent to broker for Claude adapter)
  'query',
  'controlRequest',
  // ACP input events (sent to broker for ACP adapter)
  'initialize',
  'session/new',
  'session/prompt',
  'session/cancel',
  // Codex input events (sent to broker for Codex adapter)
  'thread/settings/update',
]);
export type AxonEventType = z.infer<typeof AxonEventTypeSchema>;

/**
 * Event types that carry input INTO an agent's broker session: raw
 * prompts, control requests/responses, and ACP session lifecycle
 * messages. This is the single source of truth for which event types are
 * agent input. The server's origin classifier stamps these `USER_EVENT`,
 * and the axon publish surfaces refuse them outright so callers cannot
 * drive an agent session by publishing raw events (they must go through
 * the owner-scoped agent tools instead).
 *
 * `control_request` (snake_case, the Claude CLI wire form) and
 * `controlRequest` (camelCase, the `ClientControlRequest` envelope sent
 * to the broker) are distinct types and both are inputs — list both.
 */
export const AGENT_INPUT_EVENT_TYPES: ReadonlySet<string> = new Set([
  // Claude SDK / CLI inputs
  'query',
  'controlRequest',
  'control_request',
  'control_response',
  // ACP inputs
  'initialize',
  'session/new',
  'session/prompt',
  'session/cancel',
  // Codex inputs
  'thread/settings/update',
]);

/**
 * Prefixes for broker control-channel events (out-of-band actions such
 * as interrupt/cancel and adapter-specific control messages). Prefix
 * matched so new `broker.*` control types are input-classified by
 * default instead of silently falling through.
 */
export const AGENT_INPUT_EVENT_PREFIXES: readonly string[] = [
  'broker.control.',
  'broker.claude.',
  'broker.acp.',
];

/**
 * True when `type` is an agent-input event per
 * {@link AGENT_INPUT_EVENT_TYPES} / {@link AGENT_INPUT_EVENT_PREFIXES}.
 * Takes a plain string so callers guarding unvalidated input don't have
 * to narrow to {@link AxonEventType} first.
 */
export function isAgentInputEventType(type: string): boolean {
  return (
    AGENT_INPUT_EVENT_TYPES.has(type) ||
    AGENT_INPUT_EVENT_PREFIXES.some((prefix) => type.startsWith(prefix))
  );
}

export const AxonEventOriginValueSchema = z.enum([
  'EXTERNAL_EVENT',
  'AGENT_EVENT',
  'USER_EVENT',
  'SYSTEM_EVENT',
]);
export type AxonEventOriginValue = z.infer<typeof AxonEventOriginValueSchema>;

/**
 * True when an event is the durable user-prompt frame for one of the supported
 * agent protocols. Claude query frames predate origin tagging, while ACP and
 * native Codex share method names with non-user traffic and must be confirmed
 * by their USER_EVENT origin. Codex has two prompt shapes: `turn/start` opens
 * a turn and `turn/steer` injects into a running one — both carry
 * `params.input` and both are user prompts.
 */
export function isUserPromptEvent(event: {
  type: string;
  origin?: AxonEventOriginValue | null;
}): boolean {
  return (
    event.type === 'query' ||
    (event.type === 'session/prompt' && event.origin === 'USER_EVENT') ||
    (event.type === 'turn/start' && event.origin === 'USER_EVENT') ||
    (event.type === 'turn/steer' && event.origin === 'USER_EVENT')
  );
}

/**
 * Event types that end a turn on their type alone, across every protocol
 * and the boundaries the host forces: claude's `result` (and the
 * `turn.claude.*` enrichment forms older streams carry), native codex
 * `turn/completed`, the broker's protocol-neutral `turn.*` terminals, and
 * the devbox lifecycle events that park an open turn. The single oracle
 * shared by the server's turn-state derivation (`deriveTurnState`) and the
 * session-index boundary extractor (`extractTurnBoundaries`), so the two
 * can never disagree about what closes a turn.
 *
 * Two turn-enders are payload-discriminated and deliberately NOT in this
 * list — consumers must keep their payload checks: ACP's `session/prompt`
 * (a turn end only in its response-echo form, carrying a stop reason) and
 * claude's bare `error` (a turn end only in its structured CLI shape).
 */
export const TURN_END_EVENT_TYPES: ReadonlySet<string> = new Set([
  'result',
  'turn.claude.result',
  'turn.claude.error',
  'turn.completed',
  'turn.failed',
  'turn.cancelled',
  'turn/completed',
  'devbox.suspended',
  'devbox.shutdown',
  'devbox.failed',
]);

export const AxonEventSchema = z
  .object({
    id: z.string(),
    sequence: z.number().optional(),
    streamId: z.string(),
    type: AxonEventTypeSchema,
    payload: z.unknown(),
    timestamp: z.number(),
    origin: AxonEventOriginValueSchema.optional(),
    source: z.string().optional(),
  })
  .meta({ id: 'AxonEvent' });
export type AxonEvent = z.infer<typeof AxonEventSchema>;

export const PublishEventInputSchema = z.object({
  type: AxonEventTypeSchema,
  payload: z.unknown().optional(),
});
export type PublishEventInput = z.infer<typeof PublishEventInputSchema>;

// Runloop turn protocol payload types

export interface TurnStartedPayload {
  turnId?: string;
  turn_id?: string | number;
}

export interface TurnMessageChunkPayload {
  turnId?: string;
  turn_id?: string | number;
  content: string;
}

export interface TurnToolCallPayload {
  turnId?: string;
  turn_id?: string | number;
  toolCallId?: string;
  tool_call_id?: string;
  title?: string;
  name?: string;
  kind?: string;
  status?: string;
  input?: unknown;
}

export interface TurnToolCallUpdatePayload {
  turnId: string;
  toolCallId: string;
  output?: unknown;
  error?: string;
}

export interface TurnCompletedPayload {
  turnId?: string;
  turn_id?: string | number;
  message?: string;
  stopReason?: string;
  stop_reason?: string;
}

export interface TurnCancelledPayload {
  turnId?: string;
  turn_id?: string | number;
  reason?: string;
}

export interface TurnFailedPayload {
  turnId?: string;
  turn_id?: string | number;
  error: string;
}

/**
 * Published by the broker when the agent resumes its last turn on its own,
 * typically because a background task it started has finished; the resumed
 * turn ends with another `turn.completed`. The wire payload is `{}`, like
 * `turn.started`; the turn id rides on the axon event's `sequence_ref`.
 */
export interface TurnResumedPayload {
  turnId?: string;
  turn_id?: string | number;
}

/**
 * A background task (command, agent, or workflow) the broker is keeping the
 * devbox awake for. `kind` is open-ended so newer brokers can add kinds
 * without a contract bump.
 */
export const BackgroundTaskSchema = z.object({
  id: z.string(),
  kind: z.string(),
});
export type BackgroundTask = z.infer<typeof BackgroundTaskSchema>;

/**
 * Emitted by the broker only while no turn is running, whenever the set of
 * background tasks holding the devbox awake differs from what was last
 * published. `active` is the full set still running; empty means the
 * agent's background work is done. Origin is `SYSTEM_EVENT`, source
 * `"broker"`.
 */
export const BackgroundChangedPayloadSchema = z.object({ active: z.array(BackgroundTaskSchema) });
export type BackgroundChangedPayload = z.infer<typeof BackgroundChangedPayloadSchema>;
